import React, { useEffect, useState, useCallback } from "react";
import { groupItems } from "../../utils/printManager";
import {
  X,
  Printer,
  Receipt,
  User,
  Calendar,
  Clock,
  CreditCard,
  Banknote,
  ShoppingBag,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Tag,
  Hash,
  Wallet,
  Store,
  RotateCcw,
} from "lucide-react";
import { getSalesOrderDetail, getSalesOrders } from "../../api/salesService";
import { salesTypeService } from "../../api/masterService";
import { configService } from "../../api/configService";
import { useNotification } from "../../contexts/NotificationContext";
import { buildReceiptHtml, buildOrderReceiptHtml, getReceiptConfig, filterLabelItems, detectVehicleUnit } from "../../utils/receiptConfig";
import { executePrint, executePrintLabel } from "../../utils/printManager";
import { returnService } from "../../api/returnService";
import ReturnOrderModal from "../sales/ReturnOrderModal";
import ReturnSuccessModal from "../sales/ReturnSuccessModal";

const STATUS_CONFIG = {
  DONE: {
    label: "Selesai",
    icon: CheckCircle,
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    ring: "ring-emerald-200",
    dot: "bg-emerald-500",
  },
  RETURNED: {
    label: "Diretur Penuh",
    icon: RotateCcw,
    bg: "bg-purple-50",
    text: "text-purple-700",
    ring: "ring-purple-200",
    dot: "bg-purple-500",
  },
  PARTIAL_RETURN: {
    label: "Sebagian Diretur",
    icon: RotateCcw,
    bg: "bg-amber-50",
    text: "text-amber-700",
    ring: "ring-amber-200",
    dot: "bg-amber-500",
  },
  PENDING: {
    label: "Pending",
    icon: Clock,
    bg: "bg-amber-50",
    text: "text-amber-700",
    ring: "ring-amber-200",
    dot: "bg-amber-500",
  },
  CANCEL_REQUESTED: {
    label: "Pengajuan Batal",
    icon: AlertCircle,
    bg: "bg-teal-50",
    text: "text-teal-700",
    ring: "ring-teal-200",
    dot: "bg-teal-500",
  },
  CANCELLED: {
    label: "Dibatalkan",
    icon: XCircle,
    bg: "bg-rose-50",
    text: "text-rose-700",
    ring: "ring-rose-200",
    dot: "bg-rose-500",
  },
};

const PAYMENT_METHOD_LABEL = {
  CASH: "Tunai",
  QRIS: "QRIS",
  TRANSFER: "Transfer",
  MIXED: "Campuran",
};

const fmt = (n) => Number(n || 0).toLocaleString("id-ID");

const formatDateTime = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// Sama persis dengan logika receiptConfig.js — pakai salesTypes list untuk resolve jenis
const isAutoFromSaleType = (d, salesTypes = []) => {
  // 1. Flag eksplisit dari backend (jika ada)
  const flag = d?.discount_from_type ?? d?.totals?.discount_from_type ?? d?.meta?.discount_from_type;
  if (flag !== undefined) return !!flag;

  // 2. Cari jenis dari salesTypes list berdasarkan seller_type string
  const sellerName = String(d?.seller_type || "").trim();
  if (sellerName && salesTypes.length > 0) {
    const matched = salesTypes.find(
      (t) => String(t.name || "").trim().toLowerCase() === sellerName.toLowerCase()
    );
    if (matched) {
      return matched.jenis === "Diskon" || matched.jenis === "Markup";
    }
  }

  return false;
};

const isHideDiscount = (d, salesTypes = []) => {
  const flag = d?.discount_from_type ?? d?.totals?.discount_from_type ?? d?.meta?.discount_from_type;
  if (flag !== undefined) return !!flag;

  const sellerName = String(d?.seller_type || "").trim();
  if (sellerName && salesTypes.length > 0) {
    const matched = salesTypes.find(
      (t) => String(t.name || "").trim().toLowerCase() === sellerName.toLowerCase()
    );
    if (matched) return matched.jenis === "Diskon";
  }
  return false;
};

const isHideMarkup = (d, salesTypes = []) => {
  const flag = d?.discount_from_type ?? d?.totals?.discount_from_type ?? d?.meta?.discount_from_type;
  if (flag !== undefined) return !!flag;

  const sellerName = String(d?.seller_type || "").trim();
  if (sellerName && salesTypes.length > 0) {
    const matched = salesTypes.find(
      (t) => String(t.name || "").trim().toLowerCase() === sellerName.toLowerCase()
    );
    if (matched) return matched.jenis === "Markup";
  }
  return false;
};

// Dummy fallback data (digunakan jika API tidak return data)
const buildDummy = (tx) => ({
  id: tx?.id || "DUMMY-001",
  transaction_number: tx?.transaction_number || "TRX-DUMMY-001",
  status: "DONE",
  payment_status: "PAID",
  payment_method: "CASH",
  seller_type: "INTERNAL",
  customer_name: "Pelanggan Umum",
  created_by: "Kasir Demo",
  cashier_name: "Kasir Demo",
  created_at: new Date().toISOString(),
  notes: "Data contoh — transaksi belum terhubung ke server.",
  amount_paid: 175000,
  additional_costs: 0,
  discount: 0,
  items: [
    {
      id: 1,
      item_name: "Regium Diesel - Adaptor Caterpillar",
      variant_name: "Standar",
      quantity: 2,
      unit: "Pcs",
      unit_price: 50000,
      total_price: 100000,
    },
    {
      id: 2,
      item_name: "Filter Oli Mesin",
      variant_name: "Premium",
      quantity: 1,
      unit: "Pcs",
      unit_price: 75000,
      total_price: 75000,
    },
  ],
});

export default function ReportTransactionDetailModal({
  transaction,
  onClose,
  showSaleTypeDiscount = false,
}) {
  const { showSuccess, showError } = useNotification();
  const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usingDummy, setUsingDummy] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [salesTypes, setSalesTypes] = useState([]);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [successReturnData, setSuccessReturnData] = useState(null);
  const [storeInfo, setStoreInfo] = useState({
    name: localStorage.getItem("tenantName") || "Toko",
    address: "",
    phone: "",
    logoUrl: "",
    footerNote: "",
  });

  const fetchDetail = useCallback(async () => {
    if (!transaction) return;

    let targetId = transaction.id;
    setLoading(true);

    try {
      if (!targetId && transaction.transaction_number && transaction.transaction_number !== "-") {
        const searchRes = await getSalesOrders({ query: transaction.transaction_number, limit: 1 });
        const orders = searchRes?.data ?? searchRes;
        if (Array.isArray(orders) && orders.length > 0 && orders[0].id) {
          targetId = orders[0].id;
        }
      }

      if (!targetId) {
        setDetail(buildDummy(transaction));
        setUsingDummy(true);
        setLoading(false);
        return;
      }

      const res = await getSalesOrderDetail(targetId);
      const data = res?.data ?? res;
      if (data && (data.id || data.transaction_number)) {
        setDetail(data);
        setUsingDummy(false);
      } else {
        setDetail(buildDummy(transaction));
        setUsingDummy(true);
      }
    } catch (error) {
      setDetail(buildDummy(transaction));
      setUsingDummy(true);
    } finally {
      setLoading(false);
    }
  }, [transaction]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    let mounted = true;
    configService
      .getStore()
      .then((res) => {
        const store = res?.data ?? res ?? {};
        if (!mounted) return;
        setStoreInfo({
          name: store?.name || localStorage.getItem("tenantName") || "Toko",
          address: store?.settings?.address || store?.address || "",
          phone: store?.settings?.phone || store?.phone || "",
          logoUrl: store?.logo_url || "",
          footerNote: store?.settings?.footer_note || "",
        });
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    salesTypeService.list({ limit: 200 })
      .then((res) => {
        const list = res?.data ?? res ?? [];
        if (mounted) setSalesTypes(Array.isArray(list) ? list : (list.data ?? []));
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!transaction) return null;

  const d = detail;
  const subtotal = d?.subtotal ?? (d?.items ?? []).reduce(
    (s, i) => s + (i.total_price ?? 0),
    0,
  );
  const additionalCosts = d?.additional_costs ?? 0;
  const discount = d?.discount_total ?? d?.discount ?? 0;
  const grandTotal = d?.grand_total ?? (subtotal + additionalCosts - discount);

  const statusInfo = STATUS_CONFIG[d?.status] ?? STATUS_CONFIG.PENDING;
  const StatusIcon = statusInfo.icon;

  const paymentsArr = Array.isArray(d?.payments) ? d.payments : [];
  let methodLabel = "-";
  if (paymentsArr.length > 0) {
    const sorted = [...paymentsArr].sort((a, b) => (a.payment_sequence || 0) - (b.payment_sequence || 0));
    const labels = sorted.map(p => PAYMENT_METHOD_LABEL[p.payment_method] || p.payment_method);
    methodLabel = [...new Set(labels)].join(" + ");
  } else {
    methodLabel = PAYMENT_METHOD_LABEL[d?.payment_method] || d?.payment_method || "-";
  }

  let tunaiAmount = Number(d?.total_paid ?? d?.amount_paid ?? grandTotal);
  let dpAmount = 0;

  if (paymentsArr.length > 1) {
    const sorted = [...paymentsArr].sort((a, b) => (a.payment_sequence || 0) - (b.payment_sequence || 0));
    const last = sorted[sorted.length - 1];
    const lastAmount = last.amount != null 
      ? Number(last.amount) 
      : (Number(last.cash_amount || 0) + Number(last.electronic_amount || 0));
    dpAmount = tunaiAmount - lastAmount;
    tunaiAmount = lastAmount;
  }

  const handleViewReturnReceipt = async () => {
    try {
      const res = await returnService.listReturns({ search: d?.transaction_number, limit: 1 });
      const list = res?.data?.data || res?.data || [];
      if (Array.isArray(list) && list.length > 0) {
        const detailRes = await returnService.getReturnDetail(list[0].id);
        setSuccessReturnData(detailRes?.data ?? detailRes ?? list[0]);
      } else {
        setSuccessReturnData({
          return_number: "RTR-" + (d?.transaction_number?.replace("TRX-", "") || ""),
          transaction_number: d?.transaction_number || "-",
          return_date: d?.updated_at || d?.created_at || new Date().toISOString(),
          customer_name: d?.customer_name || "Pelanggan Umum",
          refund_method: "Tunai",
          reason: "Retur Penjualan",
          total_refund_amount: d?.grand_total || d?.subtotal || 0,
          items: (d?.items || []).map((itm) => ({
            item_name: itm.item_name || itm.name || "Item",
            quantity: itm.quantity || 1,
            unit_price: itm.unit_price || itm.price || 0,
            total_price: itm.total_price ?? ((itm.unit_price || itm.price || 0) * (itm.quantity || 1)),
          })),
        });
      }
    } catch (err) {
      console.error("Gagal memuat bukti retur:", err);
    }
  };

  const handlePrint = () => {
    if (!d) return;

    const receiptData = {
      storeName: storeInfo?.name || "",
      storeAddress: storeInfo?.address || "",
      storePhone: storeInfo?.phone || "",
      storeLogoUrl:
        storeInfo?.logoUrl ||
        localStorage.getItem("tenantLogoUrl") ||
        "",
      storeFooterNote:
        storeInfo?.footerNote || "Terima Kasih Telah Menjadi Pelanggan Kami",
      meta: {
        transactionNumber: d.transaction_number || "-",
        transactionDateTime: formatDateTime(d.created_at || d.order_date),
        cashierName: d.created_by || d.cashier_name || "-",
        customerName: d.customer_name || "Pelanggan Umum",
        customerPhone:
          d.customer_phone ||
          d.customer?.phone ||
          d.customer?.phone_number ||
          "",
        customerAddress: d.customer_address || d.customer?.address || "",
        unitName: d.unit_name || d.asset_unit_name || detectVehicleUnit(d, d.items) || "",
        sellerType: d.seller_type,
        tableName: d?.table_name || d?.selected_table_name || d?.table?.name || (d?.notes && d?.notes.match(/Meja:\s*([^\n,-]+)/)?.[1]?.trim()) || "",
        transactionNotes: d.notes || d.note || "",
      },
      items: (d.items ?? []).map((i) => {
        const rawUnit = i.unit_name || i.unit?.name || (typeof i.unit === "string" ? i.unit : "");
        const physicalSatuan =
          i.satuan ||
          i.product_unit_name ||
          i.selling_unit_name ||
          i.selling_unit?.name ||
          (typeof i.selling_unit === "string" ? i.selling_unit : "") ||
          i.product?.unit ||
          i.product?.unit_name ||
          "";
        return {
          name: i.item_name,
          qty: i.quantity,
          price: i.unit_price,
          subtotal: i.total_price, // map total to subtotal for printManager
          total: i.total_price,
          discount_amount: Number(i.discount_amount || 0),
          additional_cost: Number(i.additional_cost || 0),
          unit: rawUnit || physicalSatuan || "",
          unit_name: rawUnit || "",
          asset_unit_name: i.asset_unit_name || "",
          satuan: physicalSatuan || "",
          variant: i.variant_name,
          variant_name: i.variant_name,
          product_id: i.product_id,
          product_variant_id: i.product_variant_id,
          product_addon_id: i.product_addon_id,
          item_type: i.item_type || "PRODUCT",
          notes: i.notes || i.note || "",
        };
      }),
      totals: {
        subtotal,
        discount,
        additionalCosts: additionalCosts,
        total: grandTotal,
        dpAmount: dpAmount,
        tunai: tunaiAmount,
        payments: paymentsArr,
        paymentMethodLabel: methodLabel,
        change: d?.change_amount ?? Math.max(0, tunaiAmount - Math.max(0, grandTotal - dpAmount)),
        isDownPayment: 
          d.status === "PENDING" && 
          d.payment_status === "PARTIAL" &&
          (tunaiAmount + dpAmount) > 0 &&
          (tunaiAmount + dpAmount) < grandTotal,
        remaining: Math.max(0, grandTotal - (tunaiAmount + dpAmount)),
        discount_from_type: (showSaleTypeDiscount || transaction?.showSaleTypeDiscount) ? false : isAutoFromSaleType(d, salesTypes),
      },
    };
    
    executePrint(receiptData, 'transaction')
      .then((res) => {
        if (res.method === 'bluetooth') {
           showSuccess("Struk berhasil dikirim ke printer bluetooth");
        }
      })
      .catch((err) => {
        showError(err?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer.");
      });
  };

  const handlePrintOrderReceipt = () => {
    if (!d) return;

    const receiptData = {
      storeName: storeInfo?.name || "",
      storeFooterNote: storeInfo?.footerNote || "Dicetak untuk persiapan pesanan",
      meta: {
        transactionNumber: d.transaction_number || "-",
        transactionDateTime: formatDateTime(d.created_at || d.order_date),
        cashierName: d.created_by || d.cashier_name || "-",
        sellerType: d.seller_type,
        customerName: d.customer_name || "Pelanggan Umum",
        unitName: d.unit_name || d.asset_unit_name || detectVehicleUnit(d, d.items) || "",
        transactionNotes: d.notes || d.note || "",
      },
      items: (d.items ?? []).map((i) => ({
        name: i.item_name,
        qty: i.quantity,
        category_id: i.category_id || i.category,
        product_id: i.product_id,
        product_variant_id: i.product_variant_id,
        product_addon_id: i.product_addon_id,
        variant_name: i.variant_name,
        notes: i.notes || i.note || "",
        item_type: i.item_type || "PRODUCT",
      })),
    };
    
    executePrint(receiptData, 'order')
      .then((res) => {
        if (res.method === 'bluetooth') {
           showSuccess("Nota pesanan berhasil dikirim ke printer bluetooth");
        }
      })
      .catch((err) => {
        showError(err?.message || "Printer belum terhubung. Silakan periksa Pengaturan Printer.");
      });
  };

  const handlePrintLabels = () => {
    if (!d) return;
    const txDate = (d.created_at || d.order_date) ? new Date(d.created_at || d.order_date) : new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const datetime = `${pad(txDate.getDate())}/${pad(txDate.getMonth()+1)}/${String(txDate.getFullYear()).slice(2)} ${pad(txDate.getHours())}:${pad(txDate.getMinutes())}`;

    const storeName = storeInfo?.name || localStorage.getItem("tenantName") || "Toko";
    const customerName = d.customer_name || "Pelanggan Umum";
    const salesTypeName = d.seller_type || "";

    const printConfig = getReceiptConfig();
    const rawItems = (d.items || []).filter(it => !it.product_addon_id);
    const filteredItems = filterLabelItems(rawItems, printConfig?.labelCategories);

    if (!filteredItems || filteredItems.length === 0) {
      showError("Tidak ada produk dalam kategori label yang dipilih untuk dicetak");
      return;
    }

    const expanded = [];
    filteredItems.forEach(it => {
      const qty = Number(it.quantity || it.qty || 1);
      const unit = it.unit || it.unit_name || "";
      const productName = it.item_name || it.product_name || it.name || "-";
      const variantName = it.variant_name || "";
      const notes = it.notes || it.note || it.catatan || it.item_note || it.product_note || it.description || "";
      const addons = (d.items || [])
        .filter(a => a.product_addon_id && (a.product_id === it.product_id))
        .map(a => ({ name: a.item_name || a.name || "", qty: Number(a.quantity || a.qty || 1) }));
      for (let i = 0; i < qty; i++) {
        expanded.push({ storeName, customerName, salesTypeName, productName, variantName, qty: 1, unit, notes, addons, datetime });
      }
    });

    const total = expanded.length;
    const labelDataArray = expanded.map((e, idx) => ({ ...e, index: idx + 1, total }));

    if (!labelDataArray.length) return;
    executePrintLabel(labelDataArray)
      .then((res) => {
        if (res.method === 'bluetooth') {
          showSuccess("Label pesanan berhasil dikirim ke printer bluetooth");
        }
      })
      .catch((err) => {
        showError(err?.message || "Printer label belum terhubung. Silakan periksa Pengaturan Printer.");
      });
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden modal-panel-enter">
        {/* === HERO === */}
        <div className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-emerald-800 px-6 py-5 text-white overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-12 -left-8 w-48 h-48 bg-emerald-300/15 rounded-full blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-white/15 ring-1 ring-white/25 flex items-center justify-center flex-shrink-0">
                <Receipt className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-teal-100/80 mb-0.5">
                  Detail Transaksi
                </p>
                <h2 className="text-lg lg:text-xl font-bold leading-tight truncate">
                  {d?.transaction_number || transaction.transaction_number}
                </h2>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${statusInfo.bg} ${statusInfo.text} ${statusInfo.ring}`}>
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${statusInfo.dot}`}
                    />
                    {statusInfo.label}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-white/10 text-white ring-1 ring-white/25">
                    <ShoppingBag className="w-3 h-3" />
                    {d?.seller_type || d?.order_type_name || "Internal"}
                  </span>
                  {usingDummy && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-400/25 text-amber-50 ring-1 ring-amber-300/40">
                      <AlertCircle className="w-3 h-3" /> Data contoh
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Tutup"
              className="w-9 h-9 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors flex-shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* === BODY === */}
        <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50/40">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
              Memuat detail transaksi…
            </div>
          ) : (
            <>
              {/* Info Grid */}
              <div className="p-4 sm:p-5 grid gap-2.5 bg-white border-b border-slate-100 grid-cols-2 sm:grid-cols-4">
                <InfoCell
                  icon={Calendar}
                  label="Tanggal"
                  value={formatDateTime(d?.created_at)}
                />
                <InfoCell
                  icon={User}
                  label="Pelanggan"
                  value={d?.customer_name || "Pelanggan Umum"}
                />
                <InfoCell
                  icon={Store}
                  label="Kasir"
                  value={
                    d?.payments?.[0]?.created_by_user?.name ||
                    d?.payments?.[0]?.created_by_user?.username ||
                    d?.updated_by_user?.name ||
                    d?.updated_by_user?.username ||
                    d?.created_by_user?.name ||
                    d?.created_by_user?.username ||
                    (typeof d?.updated_by === "string" && d?.updated_by.length < 30 ? d?.updated_by : "") ||
                    (typeof d?.created_by === "string" && d?.created_by.length < 30 ? d?.created_by : "") ||
                    d?.cashier_name ||
                    (d?.source === "ECATALOG_PUBLIC" ? "E-Katalog" : "-")
                  }
                />
                <InfoCell
                  icon={d?.payment_method === "CASH" ? Banknote : CreditCard}
                  label="Metode Bayar"
                  value={methodLabel}
                />
              </div>

              {/* Items */}
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4 text-teal-600" />
                    Item Transaksi
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold">
                      {(d?.items ?? []).length}
                    </span>
                  </h3>
                </div>

                <div className="bg-white rounded-xl ring-1 ring-slate-200/70 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-10">
                            #
                          </th>
                          <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            Nama Produk
                          </th>
                          <th className="px-4 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            Qty
                          </th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            Harga
                          </th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            Subtotal
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(d?.items ?? []).length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-4 py-8 text-center text-slate-400 text-sm">
                              Tidak ada item.
                            </td>
                          </tr>
                        ) : (
                          groupItems(d.items).map((it, idx) => (
                            <React.Fragment key={it.id || idx}>
                              <tr className="hover:bg-slate-50/70">
                                <td className="px-4 py-3 text-slate-500 text-xs font-medium">
                                  {idx + 1}
                                </td>
                                <td className="px-4 py-3">
                                  <p className="font-semibold text-slate-800 leading-tight">
                                    {it.name || it.item_name}
                                  </p>
                                  {it.variant_name && (
                                    <p className="text-[11px] text-slate-500 mt-0.5">
                                      Varian: {it.variant_name}
                                    </p>
                                  )}
                                  {(it.asset_unit_name || (it.unit_name && !["pcs","liter","box","set","kg","gram","bungkus","botol","gelas","porsi","dus","lusin","meter","karung","rol"].includes(String(it.unit_name).toLowerCase())) || (it.unit && typeof it.unit === "string" && !["pcs","liter","box","set","kg","gram","bungkus","botol","gelas","porsi","dus","lusin","meter","karung","rol"].includes(String(it.unit).toLowerCase())) || it.unit?.name) && (
                                    <p className="text-[11px] text-teal-700 mt-0.5 inline-flex items-center gap-1 bg-teal-50 ring-1 ring-teal-200 px-1.5 py-0.5 rounded font-medium">
                                      Unit: {it.asset_unit_name || it.unit?.name || it.unit_name || it.unit}
                                    </p>
                                  )}
                                  {(it.notes || it.note) && (
                                    <p className="text-[11px] text-amber-700 italic font-medium mt-0.5">
                                      * Catatan: {it.notes || it.note}
                                    </p>
                                  )}
                                  {Number(it.discount_amount || 0) > 0 && (
                                    <p className="text-[11px] font-medium text-rose-600 mt-0.5">
                                      Diskon Item -Rp {fmt(it.discount_amount)}
                                    </p>
                                  )}
                                  {Number(it.additional_cost || 0) > 0 && (
                                    <p className="text-[11px] font-medium text-blue-600 mt-0.5">
                                      Biaya Tambahan Item +Rp {fmt(it.additional_cost)}
                                    </p>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 text-xs font-semibold ring-1 ring-teal-200">
                                    {it.quantity || it.qty} {it.satuan || it.product_unit_name || it.satuan_name || (["pcs","liter","box","set","kg","gram","bungkus","botol","gelas","porsi","dus","lusin","meter","karung","rol"].includes(String(it.unit_name || it.unit || '').toLowerCase()) ? (it.unit_name || it.unit) : "Pcs")}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right text-slate-700">
                                  Rp {fmt(it.unit_price || it.price)}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-slate-900">
                                  Rp {fmt(it.total_price ?? it.total ?? 0)}
                                </td>
                              </tr>
                              {it.addons && it.addons.map((ad, aIdx) => (
                                <tr key={`addon-${ad.id || aIdx}`} className="bg-slate-50/40 hover:bg-slate-50">
                                  <td className="px-4 py-2 text-slate-400 text-xs text-center"></td>
                                  <td className="px-4 py-2 pl-8">
                                    <span className="text-xs text-slate-600 font-medium italic">+ {ad.name}</span>
                                  </td>
                                  <td className="px-4 py-2 text-center">
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-medium">
                                      {ad.qty}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-right text-xs text-slate-500">
                                    Rp {fmt(ad.price)}
                                  </td>
                                  <td className="px-4 py-2 text-right text-xs font-semibold text-slate-700">
                                    Rp {fmt(ad.total)}
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Summary + Notes */}
              <div className="px-5 pb-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl ring-1 ring-slate-200/70 p-4">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" />
                    Catatan
                  </h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {(d?.notes || "")
                      .replace(/^\s*\[?\s*CANCEL\s+REQUEST\s*\]?\s*:?\s*/i, "")
                      .replace(/^\s*Meja:\s*[^\n,-]+\s*(?:-\s*)?/i, "")
                      .trim() || "—"}
                  </p>
                </div>

                <div className="bg-white rounded-xl ring-1 ring-slate-200/70 p-4">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                    <Wallet className="w-3.5 h-3.5" />
                    Ringkasan Pembayaran
                  </h4>
                  <div className="space-y-1.5 text-sm">
                    <SummaryRow
                      label="Subtotal"
                      value={`Rp ${fmt(subtotal)}`}
                    />
                    {/* [HIDDEN_SALE_TYPE_DISCOUNT]: Hide if automatic sale type discount (unless in Rekap Tipe Penjualan) */}
                    {discount > 0 && (!isHideDiscount(d, salesTypes) || showSaleTypeDiscount || transaction?.showSaleTypeDiscount) && (
                      <SummaryRow
                        label="Diskon"
                        value={`- Rp ${fmt(discount)}`}
                        valueClass="text-rose-600"
                      />
                    )}
                    {/* [HIDDEN_SALE_TYPE_MARKUP]: Hide if automatic sale type markup (unless in Rekap Tipe Penjualan) */}
                    {additionalCosts > 0 && (!isHideMarkup(d, salesTypes) || showSaleTypeDiscount || transaction?.showSaleTypeDiscount) && (
                      <SummaryRow
                        label="Biaya Tambahan"
                        value={`Rp ${fmt(additionalCosts)}`}
                      />
                    )}
                    <div className="border-t border-dashed border-slate-200 my-2" />
                    <SummaryRow
                      label="Total"
                      value={`Rp ${fmt(grandTotal)}`}
                      bold
                    />
                    {paymentsArr.length > 1 ? (
                      paymentsArr
                        .slice()
                        .sort((a, b) => (a.payment_sequence || 0) - (b.payment_sequence || 0))
                        .map((p, idx, arr) => {
                          const pMethod = PAYMENT_METHOD_LABEL[p.payment_method] || p.payment_method;
                          const pAmount = Number(p.cash_amount || 0) + Number(p.electronic_amount || 0) || Number(p.amount || 0);
                          let label = `Bayar (${pMethod})`;
                          if (idx === 0) {
                            label = `DP (${pMethod})`;
                          } else if (idx === arr.length - 1 && d?.status === "DONE") {
                            label = `Pelunasan (${pMethod})`;
                          } else {
                            label = `Pembayaran #${idx + 1} (${pMethod})`;
                          }
                          return (
                            <SummaryRow
                              key={p.id || idx}
                              label={label}
                              value={`Rp ${fmt(pAmount)}`}
                            />
                          );
                        })
                    ) : paymentsArr.length === 1 && paymentsArr[0].payment_method === "MIXED" && paymentsArr[0].cash_amount > 0 && paymentsArr[0].electronic_amount > 0 ? (
                      <>
                        <SummaryRow label="Bayar (Tunai)" value={`Rp ${fmt(paymentsArr[0].cash_amount)}`} />
                        <SummaryRow label="Bayar (Non-Tunai)" value={`Rp ${fmt(paymentsArr[0].electronic_amount)}`} />
                      </>
                    ) : (
                      <>
                        {dpAmount > 0 && (
                          <SummaryRow
                            label="DP"
                            value={`Rp ${fmt(dpAmount)}`}
                          />
                        )}
                        <SummaryRow
                          label={`Bayar (${methodLabel})`}
                          value={`Rp ${fmt(tunaiAmount)}`}
                        />
                      </>
                    )}
                    {(d?.change_amount > 0 || (dpAmount + tunaiAmount) - grandTotal > 0) && (
                      <SummaryRow
                        label="Kembalian"
                        value={`Rp ${fmt(d?.change_amount ?? ((dpAmount + tunaiAmount) - grandTotal))}`}
                        valueClass="text-emerald-600 font-semibold"
                      />
                    )}
                    {grandTotal - (dpAmount + tunaiAmount) > 0 && (
                      <SummaryRow
                        label="Sisa Tagihan"
                        value={`Rp ${fmt(grandTotal - (dpAmount + tunaiAmount))}`}
                        valueClass="text-rose-600 font-semibold"
                      />
                    )}
                    {d?.payments?.some((p) => p.payment_proof_url) && (
                      <>
                        <div className="border-t border-dashed border-slate-200 my-3" />
                        <div className="pt-1">
                          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                            Bukti Transfer
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {d.payments
                              .filter((p) => p.payment_proof_url)
                              .map((p, idx, arr) => (
                                <button
                                  type="button"
                                  key={p.id || idx}
                                  onClick={() => setPreviewImage(p.payment_proof_url)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors text-xs font-medium ring-1 ring-teal-200/50"
                                >
                                  <FileText className="w-3.5 h-3.5" />
                                  Lihat Bukti {arr.length > 1 ? `#${idx + 1}` : ''}
                                </button>
                              ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Meta footer */}
              <div className="px-5 pb-5">
                <div className="bg-white rounded-xl ring-1 ring-slate-200/70 px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    <Hash className="w-3 h-3" /> ID:{" "}
                    <span className="font-mono text-slate-700">
                      {d?.id ?? "-"}
                    </span>
                  </span>
<span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> Dibuat:{" "}
                    <span className="text-slate-700">
                      {formatDateTime(d?.created_at)}
                    </span>
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* === FOOTER === */}
        <div className="px-5 py-3.5 border-t border-slate-100 bg-slate-50/80 rounded-b-2xl flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl ring-1 ring-slate-200 text-slate-700 text-xs font-semibold hover:bg-white transition-colors order-last sm:order-first">
            Tutup
          </button>

          <div className="flex flex-wrap items-center justify-end gap-2">
            {(d?.status === "RETURNED" || d?.status === "PARTIAL_RETURN") && (
              <button
                onClick={handleViewReturnReceipt}
                disabled={loading || !d}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold transition-colors shadow-sm disabled:opacity-50">
                <RotateCcw className="w-3.5 h-3.5" />
                Lihat Bukti Retur
              </button>
            )}
            {d?.status === "DONE" && (
              <button
                onClick={() => setShowReturnModal(true)}
                disabled={loading || !d}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-xs font-semibold hover:bg-rose-100 transition-colors disabled:opacity-50">
                <RotateCcw className="w-3.5 h-3.5 text-rose-600" />
                Retur Barang
              </button>
            )}
            <button
              onClick={handlePrint}
              disabled={loading || !d}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700 transition-colors shadow-sm disabled:opacity-50">
              <Printer className="w-3.5 h-3.5" />
              Cetak Ulang Nota
            </button>
            {isApps && (
              <button
                onClick={handlePrintOrderReceipt}
                disabled={loading || !d}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-teal-500 bg-white text-teal-600 text-xs font-semibold hover:bg-teal-50 transition-colors disabled:opacity-50">
                <FileText className="w-3.5 h-3.5 text-teal-500" />
                Nota Pesanan
              </button>
            )}
            {isApps && (
              <button
                onClick={handlePrintLabels}
                disabled={loading || !d}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-emerald-500 bg-white text-emerald-700 text-xs font-semibold hover:bg-emerald-50 transition-colors disabled:opacity-50">
                <Tag className="w-3.5 h-3.5 text-emerald-600" />
                Label Pesanan
              </button>
            )}
          </div>
        </div>
      </div>

      {showReturnModal && d && (
        <ReturnOrderModal
          isOpen={showReturnModal}
          onClose={() => setShowReturnModal(false)}
          order={d}
          onSuccess={(ret) => {
            setShowReturnModal(false);
            setSuccessReturnData(ret);
          }}
        />
      )}

      {successReturnData && (
        <ReturnSuccessModal
          returnData={successReturnData}
          onClose={() => {
            setSuccessReturnData(null);
            fetchDetail();
          }}
        />
      )}

      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={(e) => {
            e.stopPropagation();
            setPreviewImage(null);
          }}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full flex flex-col items-center justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPreviewImage(null);
              }}
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src={previewImage} 
              alt="Preview Bukti Transfer" 
              className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function InfoCell({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50/60 ring-1 ring-slate-200/70 rounded-lg px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-0.5 flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {label}
      </p>
      <p className="text-sm font-semibold text-slate-800 truncate">{value}</p>
    </div>
  );
}

function SummaryRow({ label, value, bold = false, valueClass = "" }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-slate-600 ${bold ? "font-semibold" : ""}`}>
        {label}
      </span>
      <span
        className={`text-slate-900 ${bold ? "font-bold" : ""} ${valueClass}`}>
        {value}
      </span>
    </div>
  );
}
